import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, ArrowRight, Sparkles, Copy, Volume2, Eye, Rocket, PauseCircle, CreditCard, Play, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Turn } from './types';
import { apiService } from '../../services/api';

/**
 * ArchitectDeliveryCard — the Coordinator's summary card.
 *
 * Rendered inline inside the workspace chat whenever the Nexus Architect
 * closes a build turn. The Architect writes it in natural Spanish; this
 * component just lays it out with the OLED-premium aesthetic.
 *
 * Shape (turn.detail):
 * {
 *   title: string,
 *   summary: string,
 *   whats_done: string[],
 *   whats_pending: string[],
 *   why_pending: string,
 *   recommendations: string[],
 *   modules_needed: string[]
 * }
 */
export const ArchitectDeliveryCard: React.FC<{ turn: Turn; onPreview?: () => void; onPublish?: () => void }> = ({ turn, onPreview, onPublish }) => {
  const d = (turn.detail || {}) as {
    title?: string;
    summary?: string;
    whats_done?: string[];
    whats_pending?: string[];
    why_pending?: string;
    recommendations?: string[];
    modules_needed?: string[];
    paused?: boolean;
    credits_exhausted?: boolean;
    paused_reason?: string;
    // Sprint P0 · 31 Jul 2026 · transparent saldo + overdraft display.
    // Optional strings (Decimal serialised) so the UI can format them
    // safely without depending on a specific numeric type.
    balance_at_pause?: string | null;
    overdraft_used?: string | null;
    auto_resume_enabled?: boolean;
    workspace_id?: string;
    actions?: Array<{ kind: string; label: string; href?: string; endpoint?: string }>;
  };
  const title = d.title || 'Tu proyecto está listo';
  const done = d.whats_done || [];
  const pending = d.whats_pending || [];
  const recs = d.recommendations || [];
  const isPaused = Boolean(d.paused || d.credits_exhausted);

  // Sprint Final P0 · Resume + top-up handlers for the paused state.
  const [resuming, setResuming] = React.useState(false);
  const goTopUp = () => {
    try { window.location.assign('/creditos'); } catch { /* noop */ }
  };
  const wsId = d.workspace_id
    || (turn as any).refs?.workspace_id
    || (() => {
      try {
        const m = window.location.pathname.match(/\/w\/([^\/?#]+)/);
        return m ? m[1] : '';
      } catch { return ''; }
    })();
  const doResume = async () => {
    if (!wsId || resuming) return;
    setResuming(true);
    try {
      const res = await apiService.resumeArchitect(wsId);
      const bal = typeof res?.balance === 'number' ? res.balance : null;
      toast.success(
        bal !== null
          ? `Continuando tu trabajo — saldo confirmado (${bal.toFixed(2)} créditos).`
          : 'Continuando tu trabajo…',
      );
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        toast.error(detail || 'Aún no tienes créditos. Recarga primero y vuelve a pulsar Continuar.');
      } else {
        toast.error(detail || err?.message || 'No pude retomar. Vuelve a intentarlo.');
      }
    } finally {
      setResuming(false);
    }
  };

  // Sprint Cierre v1.28 · #9: iconos limpios en la esquina.
  const [playing, setPlaying] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const buildReadable = () => {
    const parts: string[] = [title];
    if (d.summary) parts.push(d.summary);
    if (done.length) parts.push('Ya está listo: ' + done.join('; '));
    if (recs.length) parts.push('Te recomiendo: ' + recs.join('; '));
    return parts.join('. ');
  };
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildReadable());
      toast.success('Copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };
  const onListen = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
      return;
    }
    try {
      setPlaying(true);
      const url = await apiService.getTTSUrl(buildReadable());
      if (!url) throw new Error('no url');
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { setPlaying(false); audioRef.current = null; };
      a.onerror = () => { setPlaying(false); audioRef.current = null; };
      await a.play();
    } catch {
      setPlaying(false);
      audioRef.current = null;
      toast.error('No se pudo reproducir');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="relative w-full max-w-[600px] rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(140deg, hsl(var(--bg-elev) / 0.92), hsl(var(--bg-elev) / 0.55))',
        border: '1px solid hsl(var(--accent) / 0.4)',
        backdropFilter: 'blur(22px) saturate(1.2)',
        boxShadow: '0 12px 44px -10px hsl(var(--accent) / 0.28), inset 0 1px 0 hsl(0 0% 100% / 0.06)',
      }}
      data-testid="architect-delivery-card"
    >
      {/* Ambient cyan glow */}
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-40 pointer-events-none"
           style={{ background: 'hsl(var(--accent) / 0.5)' }} aria-hidden />

      <div className="relative p-5 sm:p-6">
        {/* Header con iconos limpios en esquina (📋 Copiar / 🔊 Escuchar) */}
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl grid place-items-center shrink-0"
               style={{
                 background: 'linear-gradient(135deg, hsl(var(--accent) / 0.22), hsl(var(--accent) / 0.05))',
                 border: '1px solid hsl(var(--accent) / 0.5)',
                 boxShadow: '0 4px 18px hsl(var(--accent) / 0.28)',
               }}>
            <Sparkles className="w-5 h-5" style={{ color: 'hsl(var(--accent))' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] font-semibold"
                 style={{ color: 'hsl(var(--accent))' }}>
              Entrega
            </div>
            <div className="mt-0.5 text-[16px] font-semibold leading-tight"
                 style={{ color: 'hsl(var(--text))' }}
                 data-testid="architect-delivery-title">
              {title}
            </div>
            {d.summary && (
              <div className="mt-2 text-[13px] leading-relaxed"
                   style={{ color: 'hsl(var(--text-muted))' }}
                   data-testid="architect-delivery-summary">
                {d.summary}
              </div>
            )}
          </div>
          {/* Copiar + Escuchar */}
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onCopy}
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white/5 transition"
                    style={{ color: 'hsl(var(--text-muted))' }}
                    aria-label="Copiar" title="Copiar"
                    data-testid="architect-delivery-copy">
              <Copy size={15} />
            </button>
            <button onClick={onListen}
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white/5 transition"
                    style={{ color: playing ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))' }}
                    aria-label={playing ? 'Detener' : 'Escuchar'} title={playing ? 'Detener' : 'Escuchar'}
                    data-testid="architect-delivery-listen">
              <Volume2 size={15} />
            </button>
          </div>
        </div>

        {/* Resumen ejecutivo — Sprint v1.28 #10 */}
        <div className="mt-5 space-y-4">
          {done.length > 0 && (
            <Section
              icon={<CheckCircle2 className="w-3.5 h-3.5" />}
              accent="hsl(150 65% 55%)"
              label="Completado"
              items={done}
              testid="architect-done"
            />
          )}
          {pending.length > 0 && (
            <Section
              icon={<Clock className="w-3.5 h-3.5" />}
              accent="hsl(38 92% 60%)"
              label="Pendiente"
              items={pending}
              subtitle={d.why_pending}
              testid="architect-pending"
            />
          )}
          {recs.length > 0 && (
            <Section
              icon={<ArrowRight className="w-3.5 h-3.5" />}
              accent="hsl(210 100% 65%)"
              label="Recomendaciones"
              items={recs}
              testid="architect-recs"
            />
          )}
        </div>

        {/* Sprint v1.28 #9 · Solo 2 botones principales: Preview + Publicar
            Sprint Final P0 · Cuando la card está en estado `paused` (créditos
            agotados) reemplazamos los botones por Recargar créditos + Continuar. */}
        {isPaused ? (
          <div className="mt-6 flex flex-col gap-2" data-testid="architect-delivery-paused-actions">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold"
                 style={{ color: 'hsl(38 92% 60%)' }}
                 data-testid="architect-delivery-paused-badge">
              <PauseCircle size={13} /> <span>Pausado — sin créditos</span>
            </div>
            {/* Sprint P0 · 31 Jul 2026 · transparent saldo + sobregiro.
                Purely additive display so the user understands what
                was deducted before they top up. */}
            {(d.balance_at_pause !== undefined || d.overdraft_used !== undefined) && (
              <div className="rounded-lg px-3 py-2 text-xs flex flex-col gap-1"
                   style={{
                     background: 'hsl(var(--bg))',
                     border: '1px solid hsl(var(--border))',
                     color: 'hsl(var(--text-muted))',
                   }}
                   data-testid="architect-delivery-balance-detail">
                {d.balance_at_pause !== undefined && d.balance_at_pause !== null && (
                  <div className="flex items-center justify-between">
                    <span>Saldo al pausar</span>
                    <span className="font-mono font-semibold"
                          style={{ color: (Number(d.balance_at_pause) < 0)
                            ? 'hsl(0 84% 60%)' : 'hsl(var(--text))' }}>
                      {Number(d.balance_at_pause).toFixed(2)} créditos
                    </span>
                  </div>
                )}
                {d.overdraft_used && Number(d.overdraft_used) > 0 && (
                  <div className="flex items-center justify-between">
                    <span>Sobregiro utilizado</span>
                    <span className="font-mono font-semibold"
                          style={{ color: 'hsl(38 92% 60%)' }}>
                      -{Number(d.overdraft_used).toFixed(2)} créditos
                    </span>
                  </div>
                )}
                {d.auto_resume_enabled && (
                  <div className="text-[10.5px] mt-1 opacity-80">
                    Cuando recargues créditos, el trabajo se reanudará automáticamente.
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={goTopUp}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-medium transition"
                style={{
                  background: 'hsl(var(--bg))',
                  border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--text))',
                }}
                data-testid="architect-delivery-topup"
              >
                <CreditCard size={14} /> Recargar créditos
              </button>
              <button
                onClick={doResume}
                disabled={resuming}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold transition disabled:opacity-60"
                style={{
                  background: 'hsl(var(--accent))',
                  color: 'hsl(210 40% 8%)',
                  boxShadow: '0 6px 20px -6px hsl(var(--accent) / 0.6)',
                }}
                data-testid="architect-delivery-resume"
              >
                {resuming
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Play size={14} />}
                {resuming ? 'Continuando…' : 'Continuar'}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-2">
            <button
              onClick={onPreview}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-medium transition"
              style={{
                background: 'hsl(var(--bg))',
                border: '1px solid hsl(var(--border))',
                color: 'hsl(var(--text))',
              }}
              data-testid="architect-delivery-preview"
            >
              <Eye size={14} /> Preview
            </button>
            <button
              onClick={onPublish}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold transition"
              style={{
                background: 'hsl(var(--accent))',
                color: 'hsl(210 40% 8%)',
                boxShadow: '0 6px 20px -6px hsl(var(--accent) / 0.6)',
              }}
              data-testid="architect-delivery-publish"
            >
              <Rocket size={14} /> Publicar
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Section: React.FC<{
  icon: React.ReactNode;
  accent: string;
  label: string;
  items: string[];
  subtitle?: string;
  testid: string;
}> = ({ icon, accent, label, items, subtitle, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold"
         style={{ color: accent }}>
      {icon}<span>{label}</span>
    </div>
    <ul className="mt-2 space-y-1.5 text-[13px]" style={{ color: 'hsl(var(--text))' }}>
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 leading-snug">
          <span className="mt-2 w-1 h-1 rounded-full shrink-0" style={{ background: accent }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
    {subtitle && (
      <div className="mt-2 text-[11px] italic opacity-70"
           style={{ color: 'hsl(var(--text-muted))' }}>
        {subtitle}
      </div>
    )}
  </div>
);
